<div class="min-h-screen">
  <div class="py-12 sm:py-20">
    <div class="mx-auto max-w-7xl px-6 lg:px-8">
      <!-- Hero Section -->
      <div class="text-center mb-16">
        <h1
          class="text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100 sm:text-6xl"
        >
          {{ "SEARCH.TITLE" | translate }}
        </h1>
        <p class="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
          {{ "APP.TAGLINE" | translate }}
        </p>
      </div>

      <!-- Search Box -->
      <div class="mx-auto max-w-2xl">
        <div class="flex rounded-md shadow-sm">
          <input
            type="text"
            name="searchTerm"
            id="searchTerm"
            [formControl]="form.controls.searchTerm"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-arabic-green focus:border-arabic-green dark:bg-gray-700 dark:text-white bg-white"
            placeholder="{{ 'SEARCH.PLACEHOLDER' | translate }}"
            (keyup.enter)="search()"
          />
          <button
            type="button"
            (click)="search()"
            class="rounded-r-md bg-arabic-green py-4 px-6 text-md font-semibold text-white hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arabic-green cursor-pointer"
          >
            {{ "SEARCH.BUTTON" | translate }}
          </button>
        </div>
        <div class="mt-4 text-right">
          <button
            type="button"
            (click)="toggleAdvancedSearch()"
            class="text-sm text-arabic-green hover:underline focus:outline-none cursor-pointer"
          >
            {{
              showAdvancedSearch
                ? ("SEARCH.HIDE_ADVANCED_SEARCH" | translate)
                : ("SEARCH.ADVANCED_SEARCH" | translate)
            }}
          </button>
        </div>
      </div>

      <!-- Advanced Search Filters -->
      <div *ngIf="showAdvancedSearch" class="mt-8 mx-auto max-w-4xl">
        <form [formGroup]="form" *ngIf="form">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3
              class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4"
            >
              {{ "SEARCH.ADVANCED_SEARCH" | translate }}
            </h3>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <!-- Gender Filter -->
              <div>
                <app-gender
                  id="genderFilter"
                  name="genderFilter"
                  [formControl]="form.controls.gender"
                  label="GRAVE.GENDER"
                ></app-gender>
              </div>

              <!-- Zone Filter -->
              <div>
                <app-zone
                  id="zoneFilter"
                  name="zoneFilter"
                  [formControl]="form.controls.zone"
                  label="GRAVE.ZONE"
                ></app-zone>
              </div>

              <!-- Grave Type Filter -->
              <div>
                <app-grave-type
                  id="graveTypeFilter"
                  name="graveTypeFilter"
                  [formControl]="form.controls.graveType"
                  label="GRAVE_FORM.GRAVE_TYPE"
                ></app-grave-type>
              </div>
            </div>

            <!-- Date Filters - Full Row -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <!-- Date of Birth Filter -->
              <div>
                <label
                  for="birthDateFilter"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  {{ "GRAVE.BIRTH" | translate }}
                </label>
                <app-date-picker
                  [formControl]="form.controls.birthDate"
                ></app-date-picker>
              </div>

              <!-- Date of Death Filter -->
              <div>
                <label
                  for="deathDateFilter"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  {{ "GRAVE.DEATH" | translate }}
                </label>
                <app-date-picker
                  [formControl]="form.controls.deathDate"
                ></app-date-picker>
              </div>
            </div>

            <!-- Filter Actions -->
            <div class="flex justify-end mt-4 space-x-3">
              <button
                (click)="resetFilters()"
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arabic-green cursor-pointer"
              >
                {{ "FILTER.RESET" | translate }}
              </button>
              <button
                (click)="applyFilters()"
                class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-arabic-green hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arabic-green cursor-pointer"
              >
                {{ "FILTER.APPLY" | translate }}
              </button>
            </div>
          </div>
        </form>
      </div>

      <!-- Loading Indicator -->
      <div *ngIf="loading" class="mt-12 text-center">
        <div
          class="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-arabic-green border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
        ></div>
        <p class="mt-4 text-gray-600 dark:text-gray-400">
          {{ "SEARCH.LOADING" | translate }}
        </p>
      </div>

      <!-- No Results Message -->
      <div *ngIf="noResults && !loading" class="mt-12 text-center">
        <p class="text-lg text-gray-600 dark:text-gray-400">
          {{ "SEARCH.NO_RESULTS" | translate }}
        </p>
      </div>

      <!-- Search Results -->
      <div *ngIf="graves.length > 0 && !loading" class="mt-12">
        <h2 class="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">
          {{ "SEARCH.RESULTS" | translate }}
        </h2>

        <!-- Grid Layout for Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            *ngFor="let grave of graves"
            class="flex flex-col rounded-lg shadow-md overflow-hidden hover:shadow-lg transition duration-300 bg-white dark:bg-gray-700"
          >
            <!-- Card Image -->
            <div
              *ngIf="grave.imageURLBase64"
              class="h-48 w-full bg-gray-100 dark:bg-gray-800 overflow-hidden"
            >
              <img
                [src]="'data:image/jpg;base64,' + grave.imageURLBase64"
                [alt]="grave.fullNameEn"
                class="w-full h-full object-cover"
              />
            </div>
            <div
              *ngIf="!grave.imageURLBase64"
              class="h-48 w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-20 w-20 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1"
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
            </div>

            <!-- Card Content -->
            <div class="flex-grow p-5 flex flex-col">
              <p
                *ngIf="grave.refID"
                class="text-xl font-medium text-arabic-green mb-2"
              >
                {{ grave.refID }}
              </p>

              <!-- Name display based on language -->
              <ng-container *ngIf="isArabicLanguage(); else englishPrimary">
                <!-- Arabic is primary, English is secondary -->
                <h3
                  class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 text-right"
                  dir="rtl"
                >
                  {{ grave.fullNameAr }}
                </h3>
                <p class="text-gray-600 dark:text-gray-300 mb-3">
                  {{ grave.fullNameEn }}
                </p>
              </ng-container>
              <ng-template #englishPrimary>
                <!-- English is primary, Arabic is secondary -->
                <h3
                  class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2"
                >
                  {{ grave.fullNameEn }}
                </h3>
                <p
                  class="text-gray-600 dark:text-gray-300 mb-3 text-right"
                  dir="rtl"
                >
                  {{ grave.fullNameAr }}
                </p>
              </ng-template>

              <div
                class="text-sm text-gray-500 dark:text-gray-400 space-y-1 mb-3"
              >
                <p *ngIf="grave.dateOfBirth">
                  {{ "GRAVE.BIRTH" | translate }}:
                  {{ grave.dateOfBirth | date }}
                </p>
                <p *ngIf="grave.dateOfDeath">
                  {{ "GRAVE.DEATH" | translate }}:
                  {{ grave.dateOfDeath | date }}
                </p>
                <p *ngIf="grave.zGender">
                  {{ "GRAVE.GENDER" | translate }}:
                  {{
                    isArabicLanguage()
                      ? grave.zGender.nameAr
                      : grave.zGender.nameEn
                  }}
                </p>
                <p *ngIf="grave.zZone">
                  {{ "GRAVE.ZONE" | translate }}:
                  {{
                    isArabicLanguage() ? grave.zZone.nameAr : grave.zZone.nameEn
                  }}
                </p>
                <p *ngIf="grave.zGraveType">
                  {{ "GRAVE_FORM.GRAVE_TYPE" | translate }}:
                  {{
                    isArabicLanguage()
                      ? grave.zGraveType.nameAr
                      : grave.zGraveType.nameEn
                  }}
                </p>
              </div>

              <!-- <div *ngIf="grave.description" class="mb-4">
                <p class="text-gray-700 dark:text-gray-300 line-clamp-2">
                  {{
                    isArabicLanguage() ? grave.description : grave.descriptionEn
                  }}
                </p>
              </div> -->

              <div class="mt-auto">
                <a
                  [routerLink]="['/search', grave.graveId]"
                  class="inline-block w-full text-center bg-emerald-600 text-white py-2 px-4 rounded text-sm font-medium hover:bg-emerald-700 transition-colors"
                >
                  {{ "GRAVE.VIEW_DETAILS" | translate }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div
          *ngIf="
            pagination &&
            pagination.totalPages > 0 &&
            pagination.totalItems > 0 &&
            !loading &&
            graves.length > 0
          "
          class="mt-10"
        >
          <div
            class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between"
          >
            <div>
              <p class="text-sm text-gray-700 dark:text-gray-300">
                {{ getDisplayedRange() }}
              </p>
            </div>
            <div>
              <nav
                class="isolate inline-flex rounded-md shadow-sm"
                aria-label="Pagination"
              >
                <!-- Previous -->
                <button
                  (click)="pageChanged(pagination.currentPage - 1)"
                  [disabled]="pagination.currentPage === 1"
                  class="relative inline-flex items-center cursor-pointer bg-white dark:bg-gray-700 rounded-s-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 hover:text-arabic-green dark:hover:bg-gray-800 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span class="sr-only">{{
                    "SEARCH.PREVIOUS" | translate
                  }}</span>
                  <svg
                    class="h-5 w-5 rtl:rotate-180"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>

                <!-- Page numbers -->
                <ng-container *ngFor="let page of getPageNumbers()">
                  <button
                    *ngIf="page !== -1"
                    (click)="pageChanged(page)"
                    [disabled]="page === pagination.currentPage"
                    [ngClass]="{
                      'bg-emerald-100 dark:bg-emerald-800 text-gray-900 dark:text-white': page === pagination.currentPage,
                      'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-arabic-green': page !== pagination.currentPage
                    }"
                    class="relative inline-flex cursor-pointer items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:z-20 focus:outline-offset-0 transition-colors disabled:cursor-not-allowed"
                  >
                    {{ page }}
                  </button>
                  <span
                    *ngIf="page === -1"
                    class="relative inline-flex items-center bg-white dark:bg-gray-700 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600"
                  >
                    ...
                  </span>
                </ng-container>

                <!-- Next -->
                <button
                  (click)="pageChanged(pagination.currentPage + 1)"
                  [disabled]="pagination.currentPage === pagination.totalPages"
                  class="relative inline-flex cursor-pointer bg-white dark:bg-gray-700 items-center rounded-e-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 hover:text-arabic-green dark:hover:bg-gray-800 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span class="sr-only">{{ "SEARCH.NEXT" | translate }}</span>
                  <svg
                    class="h-5 w-5 rtl:rotate-180"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </nav>
            </div>
          </div>

          <!-- Mobile pagination (simplified) -->
          <div class="flex justify-center sm:hidden mt-4">
            <nav class="flex gap-2" aria-label="Pagination">
              <button
                (click)="pageChanged(pagination.currentPage - 1)"
                [disabled]="pagination.currentPage === 1"
                class="relative inline-flex items-center rounded-md px-3 py-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 hover:text-arabic-green dark:hover:bg-gray-800 focus:z-20 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  class="h-5 w-5 rtl:rotate-180"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
              <p class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-0">
                {{ pagination.currentPage }} / {{ pagination.totalPages }}
              </p>
              <button
                (click)="pageChanged(pagination.currentPage + 1)"
                [disabled]="pagination.currentPage === pagination.totalPages"
                class="relative inline-flex items-center rounded-md px-3 py-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 hover:text-arabic-green dark:hover:bg-gray-800 focus:z-20 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  class="h-5 w-5 rtl:rotate-180"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>

      <!-- Features Section -->
      <!-- <div class="mt-20">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div
            class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 hover:shadow-lg transition duration-300"
          >
            <h2
              class="text-xl font-semibold mb-3 text-gray-900 dark:text-gray-100"
            >
              {{ "CEMETERY_MAP.TITLE" | translate }}
            </h2>
            <p class="text-gray-600 dark:text-gray-300 mb-4">
              {{ "CEMETERY_MAP.INSTRUCTIONS" | translate }}
            </p>
            <a href="#" class="text-arabic-green hover:underline"
              >{{ "MENU.CEMETERY_MAP" | translate }} →</a
            >
          </div>

          <div
            class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 hover:shadow-lg transition duration-300"
          >
            <h2
              class="text-xl font-semibold mb-3 text-gray-900 dark:text-gray-100"
            >
              {{ "MEMORIAL_SERVICES.TITLE" | translate }}
            </h2>
            <p class="text-gray-600 dark:text-gray-300 mb-4">
              {{ "MEMORIAL_SERVICES.DESCRIPTION" | translate }}
            </p>
            <a href="#" class="text-arabic-green hover:underline"
              >{{ "MENU.MEMORIAL_SERVICES" | translate }} →</a
            >
          </div>

          <div
            class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 hover:shadow-lg transition duration-300"
          >
            <h2
              class="text-xl font-semibold mb-3 text-gray-900 dark:text-gray-100"
            >
              {{ "ABOUT_US.TITLE" | translate }}
            </h2>
            <p class="text-gray-600 dark:text-gray-300 mb-4">
              {{ "ABOUT_US.MISSION" | translate }}
            </p>
            <a href="#" class="text-arabic-green hover:underline"
              >{{ "MENU.ABOUT_US" | translate }} →</a
            >
          </div>
        </div>
      </div> -->
    </div>
  </div>
</div>
